import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  UserCog, 
  Plus, 
  Users, 
  Edit3, 
  Trash2,
  Mail,
  Calendar,
  Shield,
  User,
  Eye,
  EyeOff,
  AlertTriangle,
  Key,
  ShieldCheck,
  Clock
} from 'lucide-react';
import axios from 'axios';
import PasswordModal from './PasswordModal';
import PermissionsModal from './PermissionsModal';
import TeamManagement from './TeamManagement';
import AttendancePage from './AttendancePage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StaffPage = () => {
  const { user } = useContext(AuthContext);
  const { t, tt } = useLanguage();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'sales_manager'
  });
  const [errors, setErrors] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState({
    id: '',
    name: '',
    email: '',
    role: 'team_lead_sales'
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordChangeUser, setPasswordChangeUser] = useState(null);
  const [permissionsUser, setPermissionsUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showMasterPasswordModal, setShowMasterPasswordModal] = useState(false);
  const [pendingUserUpdate, setPendingUserUpdate] = useState(null);
  const [originalUserRole, setOriginalUserRole] = useState(null);
  // [STAFF-TABS-2026-07-17-REBUILD] Restored 3-tab layout: Employees / Teams / Attendance
  const [activeTab, setActiveTab] = useState('employees');

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users`);
      // Filter out clients - only show staff members
      const staffOnly = response.data.filter(user => 
        user.role !== 'client' && user.role !== 'CLIENT'
      );
      setStaff(staffOnly);
    } catch (error) {
      console.error('Error fetching staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setErrors({});

    // Basic validation
    const newErrors = {};
    if (!newUser.name.trim()) newErrors.name = 'Обязательное поле';
    if (!newUser.email.trim()) newErrors.email = 'Обязательное поле';
    if (!newUser.password.trim()) newErrors.password = 'Обязательное поле';
    if (newUser.password.length < 6) newErrors.password = 'Минимум 6 символов';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await axios.post(`${API}/auth/register`, newUser);
      setShowAddModal(false);
      setNewUser({ name: '', email: '', password: '', role: 'sales_manager' });
      fetchStaff();
    } catch (error) {
      console.error('Staff creation error:', error.response?.data);
      
      if (error.response?.data?.detail) {
        // Handle Pydantic validation errors which can be array of objects
        let errorMessage;
        if (Array.isArray(error.response.data.detail)) {
          // Extract messages from Pydantic validation error objects
          errorMessage = error.response.data.detail
            .map(err => err.msg || err.message || JSON.stringify(err))
            .join('. ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else {
          errorMessage = JSON.stringify(error.response.data.detail);
        }
        setErrors({ general: errorMessage });
      } else {
        setErrors({ general: 'Ошибка при создании пользователя' });
      }
    }
  };

  const handleEditUser = (user) => {
    setEditingUser({
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role
    });
    setOriginalUserRole(user.role); // Store original role
    setShowEditModal(true);
    setErrors({});
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setErrors({});

    // Basic validation
    const newErrors = {};
    if (!editingUser.name.trim()) newErrors.name = 'Обязательное поле';
    if (!editingUser.email.trim()) newErrors.email = 'Обязательное поле';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Check if editing a superadmin (based on ORIGINAL role) - require password
    if (originalUserRole === 'superadmin') {
      setPendingUserUpdate(editingUser);
      setShowEditModal(false);
      setShowMasterPasswordModal(true);
      return;
    }

    // If not superadmin, proceed with update
    await performUserUpdate(editingUser);
  };

  const performUserUpdate = async (userData) => {
    try {
      await axios.put(`${API}/users/${userData.id}`, {
        name: userData.name,
        email: userData.email,
        role: userData.role
      });
      setShowEditModal(false);
      setEditingUser({ id: '', name: '', email: '', role: 'sales_manager' });
      setPendingUserUpdate(null);
      fetchStaff();
    } catch (error) {
      console.error('Staff update error:', error.response?.data);
      
      if (error.response?.data?.detail) {
        // Handle Pydantic validation errors which can be array of objects
        let errorMessage;
        if (Array.isArray(error.response.data.detail)) {
          // Extract messages from Pydantic validation error objects
          errorMessage = error.response.data.detail
            .map(err => err.msg || err.message || JSON.stringify(err))
            .join('. ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else {
          errorMessage = JSON.stringify(error.response.data.detail);
        }
        setErrors({ general: errorMessage });
      } else {
        setErrors({ general: 'Ошибка при обновлении пользователя' });
      }
    }
  };

  const handleDeleteUser = (userId, userName) => {
    console.log('HandleDeleteUser called with userId:', userId);
    setUserToDelete({ id: userId, name: userName });
    setShowDeleteModal(true);
  };

  const handleChangePassword = (member) => {
    setPasswordChangeUser(member);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  // State for pending password change (for superadmin protection)
  const [pendingPasswordChange, setPendingPasswordChange] = useState(null);

  const confirmChangePassword = async () => {
    if (!passwordChangeUser || !newPassword) return;

    if (newPassword.length < 6) {
      alert(tt('Пароль должен содержать минимум 6 символов'));
      return;
    }

    // Check if changing password for superadmin - require master password
    if (passwordChangeUser.role === 'superadmin') {
      setPendingPasswordChange({ user: passwordChangeUser, password: newPassword });
      setShowPasswordModal(false);
      setShowMasterPasswordModal(true);
      return;
    }

    // If not superadmin, proceed directly
    await performPasswordChange(passwordChangeUser, newPassword);
  };

  const performPasswordChange = async (targetUser, password) => {
    try {
      await axios.put(`${API}/users/${targetUser.id}/password`, {
        new_password: password
      });
      
      alert(`Пароль для ${targetUser.name} успешно изменен`);
      setShowPasswordModal(false);
      setPasswordChangeUser(null);
      setNewPassword('');
      setPendingPasswordChange(null);
    } catch (error) {
      console.error('Password change error:', error);
      alert(error.response?.data?.detail || 'Ошибка при смене пароля');
    }
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    console.log('User confirmed deletion, making API call');
    
    try {
      const response = await axios.delete(`${API}/users/${userToDelete.id}`);
      console.log('Delete response:', response);
      setShowDeleteModal(false);
      setUserToDelete(null);
      fetchStaff();
      console.log('Staff list refreshed');
    } catch (error) {
      console.error('Staff delete error:', error.response?.data);
      console.error('Full error object:', error);
      setErrors({ general: error.response?.data?.detail || 'Ошибка при удалении сотрудника' });
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  const getRoleBadge = (role) => {
    if (role === 'superadmin') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <Shield className="w-3 h-3 mr-1" />
          {tt('Суперадмин')}
        </span>
      );
    }
    if (role === 'admin') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Shield className="w-3 h-3 mr-1" />
          {tt('Админ')}
        </span>
      );
    }
    if (role === 'team_lead_sales') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          <User className="w-3 h-3 mr-1" />
          {tt('Тим лидер Сейл')}
        </span>
      );
    }
    if (role === 'sales_manager') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <User className="w-3 h-3 mr-1" />
          {tt('Сейл менеджер')}
        </span>
      );
    }
    if (role === 'team_lead_retention') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          <User className="w-3 h-3 mr-1" />
          {tt('Тим лидер Ретеншн')}
        </span>
      );
    }
    if (role === 'retention_manager') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <User className="w-3 h-3 mr-1" />
          {tt('Ретеншн менеджер')}
        </span>
      );
    }
    // Fallback for old data
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <User className="w-3 h-3 mr-1" />
        {tt('Менеджер')}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewUser({ ...newUser, password });
  };

  // Admin and superadmin can access this page
  if (user?.role !== 'superadmin' && user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{tt('Доступ запрещен')}</h2>
          <p className="text-gray-600 mb-6">{tt('Только администратор может управлять сотрудниками')}</p>
          <Link to="/" className="btn btn-primary">
            {tt('Вернуться на главную')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
        {/* Page Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">{tt('Управление сотрудниками')}</h1>
            <p className="page-subtitle">{tt('Создание и управление аккаунтами персонала')}</p>
          </div>
          {activeTab === 'employees' && (
            <button
              onClick={() => setShowAddModal(true)}
              className="btn btn-primary"
              data-testid="add-staff-button"
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
              {tt('Добавить')}
            </button>
          )}
        </div>

        {/* [STAFF-TABS-2026-07-17-REBUILD] Tab bar */}
        <div className="flex gap-1 mb-4 border-b border-gray-200 dark:border-gray-700">
          {[
            { k: 'employees', label: tt('Сотрудники'), Icon: Users },
            { k: 'teams', label: tt('Команды'), Icon: Users },
            { k: 'attendance', label: tt('Учёт времени'), Icon: Clock },
          ].map(({ k, label, Icon }) => (
            <button
              key={k}
              onClick={() => setActiveTab(k)}
              className={`px-4 py-2 flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                activeTab === k
                  ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 font-semibold'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              data-testid={`tab-${k}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'teams' && <TeamManagement />}
        {activeTab === 'attendance' && <AttendancePage />}

        {activeTab === 'employees' && (
          <>
        {/* Staff Table */}
        <div className="content-card overflow-hidden" data-testid="staff-table">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <div className="spinner mr-3"></div>
              <span className="text-gray-600">{tt('Загрузка сотрудников...')}</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{tt('Сотрудник')}</th>
                    <th>Email</th>
                    <th>{tt('Роль')}</th>
                    <th>{tt('Дата создания')}</th>
                    <th>{tt('Статистика')}</th>
                    <th>{tt('Действия')}</th>
                  </tr>
                </thead>
                <tbody>
                  {staff.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-gray-500">
                        <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>{tt('Сотрудники не найдены')}</p>
                      </td>
                    </tr>
                  ) : (
                    staff.map((member) => (
                      <tr key={member.id} className="hover:bg-gray-50">
                        <td>
                          <div className="flex items-center">
                            <div className="p-2 bg-gray-100 rounded-full mr-3">
                              <User className="w-5 h-5 text-gray-600" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{member.name}</div>
                              <div className="text-sm text-gray-500">ID: {String(member.id).slice(0, 8)}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center text-gray-900">
                            <Mail className="w-4 h-4 mr-2 text-gray-400" />
                            {member.email}
                          </div>
                        </td>
                        <td>{getRoleBadge(member.role)}</td>
                        <td>
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="w-4 h-4 mr-2" />
                            {formatDate(member.created_at)}
                          </div>
                        </td>
                        <td>
                          <div className="text-sm text-gray-600">
                            {member.role === 'manager' ? (
                              <div>
                                <div>{tt('Клиентов: 0')}</div>
                                <div>{tt('Активных: 0')}</div>
                              </div>
                            ) : (
                              <div className="text-gray-400">-</div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleEditUser(member)}
                              className="btn btn-secondary p-2"
                              title={tt('Редактировать')}
                              data-testid={`edit-staff-${member.id}`}
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            {user?.role === 'superadmin' && (
                              <button
                                onClick={() => handleChangePassword(member)}
                                className="btn btn-secondary p-2 bg-yellow-50 hover:bg-yellow-100 text-yellow-600"
                                title={tt('Сменить пароль')}
                                data-testid={`change-password-${member.id}`}
                              >
                                <Key className="w-4 h-4" />
                              </button>
                            )}
                            {member.role !== 'superadmin' && (user?.role === 'superadmin' || user?.role === 'admin') && (
                              <button
                                onClick={() => setPermissionsUser(member)}
                                className="btn btn-secondary p-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-600"
                                title={tt('Права доступа')}
                                data-testid={`permissions-${member.id}`}
                              >
                                <ShieldCheck className="w-4 h-4" />
                              </button>
                            )}
                            {member.role !== 'superadmin' && (
                              <button
                                onClick={() => handleDeleteUser(member.id, member.name)}
                                className="btn btn-danger p-2"
                                title={tt('Удалить')}
                                data-testid={`delete-staff-${member.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card p-4">
            <div className="text-2xl font-bold text-blue-600">{staff.length}</div>
            <div className="text-sm text-gray-600">{tt('Всего сотрудников')}</div>
          </div>
          <div className="card p-4">
            <div className="text-2xl font-bold text-green-600">
              {staff.filter(s => 
                ['manager', 'sales_manager', 'retention_manager', 'team_lead_sales', 'team_lead_retention'].includes(s.role)
              ).length}
            </div>
            <div className="text-sm text-gray-600">{tt('Менеджеров')}</div>
          </div>
          <div className="card p-4">
            <div className="text-2xl font-bold text-yellow-600">
              {staff.filter(s => s.role === 'admin').length}
            </div>
            <div className="text-sm text-gray-600">{tt('Админов')}</div>
          </div>
          <div className="card p-4">
            <div className="text-2xl font-bold text-red-600">
              {staff.filter(s => s.role === 'superadmin').length}
            </div>
            <div className="text-sm text-gray-600">{tt('Суперадминов')}</div>
          </div>
        </div>
          </>
        )}

      {/* Add Staff Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="add-staff-modal">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{tt('Добавить сотрудника')}</h3>
            
            {errors.general && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
                {errors.general}
              </div>
            )}
            
            <form onSubmit={handleCreateUser}>
              <div className="space-y-4">
                <div className="form-group">
                  <label className="form-label">{tt('Полное имя')}</label>
                  <input
                    type="text"
                    value={newUser.name}
                    onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                    className={`form-input ${errors.name ? 'border-red-500' : ''}`}
                    placeholder={tt('Иван Петров')}
                    data-testid="input-name"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    className={`form-input ${errors.email ? 'border-red-500' : ''}`}
                    placeholder="ivan@forex.com"
                    data-testid="input-email"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>

                <div className="form-group">
                  <label className="form-label">{tt('Пароль')}</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newUser.password}
                      onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                      className={`form-input pr-20 ${errors.password ? 'border-red-500' : ''}`}
                      placeholder={tt('Минимум 6 символов')}
                      data-testid="input-password"
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-gray-500 hover:text-gray-700 p-1"
                        data-testid="toggle-password"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button
                        type="button"
                        onClick={generatePassword}
                        className="text-blue-500 hover:text-blue-700 p-1 text-xs"
                        title={tt('Генерировать')}
                        data-testid="generate-password"
                      >
                        Gen
                      </button>
                    </div>
                  </div>
                  {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                </div>

                <div className="form-group">
                  <label className="form-label">{tt('Роль')}</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                    className="form-select"
                    data-testid="select-role"
                  >
                    <option value="team_lead_sales">{tt('Тим лидер Сейл')}</option>
                    <option value="sales_manager">{tt('Сейл менеджер')}</option>
                    <option value="team_lead_retention">{tt('Тим лидер Ретеншн')}</option>
                    <option value="retention_manager">{tt('Ретеншн менеджер')}</option>
                    <option value="admin">{tt('Админ')}</option>
                    <option value="superadmin">{tt('Суперадмин')}</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewUser({ name: '', email: '', password: '', role: 'team_lead_sales' });
                    setErrors({});
                  }}
                  className="btn btn-secondary"
                  data-testid="cancel-add-staff"
                >
                  {tt('Отмена')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  data-testid="submit-add-staff"
                >
                  {tt('Создать')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Staff Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="edit-staff-modal">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{tt('Редактировать сотрудника')}</h3>
            
            {errors.general && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
                {errors.general}
              </div>
            )}
            
            <form onSubmit={handleUpdateUser}>
              <div className="space-y-4">
                <div className="form-group">
                  <label className="form-label">{tt('Полное имя')}</label>
                  <input
                    type="text"
                    value={editingUser.name}
                    onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                    className={`form-input ${errors.name ? 'border-red-500' : ''}`}
                    placeholder={tt('Иван Петров')}
                    data-testid="edit-input-name"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    value={editingUser.email}
                    onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                    className={`form-input ${errors.email ? 'border-red-500' : ''}`}
                    placeholder="ivan@forex.com"
                    data-testid="edit-input-email"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>

                <div className="form-group">
                  <label className="form-label">{tt('Роль')}</label>
                  <select
                    value={editingUser.role}
                    onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                    className="form-select"
                    data-testid="edit-select-role"
                  >
                    <option value="team_lead_sales">{tt('Тим лидер Сейл')}</option>
                    <option value="sales_manager">{tt('Сейл менеджер')}</option>
                    <option value="team_lead_retention">{tt('Тим лидер Ретеншн')}</option>
                    <option value="retention_manager">{tt('Ретеншн менеджер')}</option>
                    <option value="admin">{tt('Админ')}</option>
                    <option value="superadmin">{tt('Суперадмин')}</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingUser({ id: '', name: '', email: '', role: 'sales_manager' });
                    setErrors({});
                  }}
                  className="btn btn-secondary"
                  data-testid="cancel-edit-staff"
                >
                  {tt('Отмена')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  data-testid="submit-edit-staff"
                >
                  {tt('Обновить')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showPasswordModal && passwordChangeUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="change-password-modal">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 mx-auto bg-yellow-100 rounded-full flex items-center justify-center">
                <Key className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
            
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{tt('Сменить пароль')}</h3>
              <p className="text-sm text-gray-600">
                {tt('Сменить пароль для сотрудника')} <strong>{passwordChangeUser.name}</strong>
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">{tt('Новый пароль')}</label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input-field pr-10"
                  placeholder={tt('Минимум 6 символов')}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showNewPassword ? (
                    <EyeOff className="w-5 h-5 text-gray-400" />
                  ) : (
                    <Eye className="w-5 h-5 text-gray-400" />
                  )}
                </button>
              </div>
              {newPassword && newPassword.length < 6 && (
                <p className="text-xs text-red-600 mt-1">{tt('Пароль должен содержать минимум 6 символов')}</p>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordChangeUser(null);
                  setNewPassword('');
                }}
                className="btn btn-secondary"
                data-testid="cancel-change-password"
              >
                {tt('Отмена')}
              </button>
              <button
                onClick={confirmChangePassword}
                className="btn btn-primary bg-yellow-600 hover:bg-yellow-700"
                disabled={!newPassword || newPassword.length < 6}
                data-testid="confirm-change-password"
              >
                <Key className="w-4 h-4 mr-2" />
                {tt('Сменить пароль')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Master Password Modal for Superadmin Edit/Password Protection */}
      <PasswordModal
        isOpen={showMasterPasswordModal}
        onClose={() => {
          setShowMasterPasswordModal(false);
          setPendingUserUpdate(null);
          setPendingPasswordChange(null);
        }}
        onConfirm={() => {
          if (pendingUserUpdate) {
            performUserUpdate(pendingUserUpdate);
          }
          if (pendingPasswordChange) {
            performPasswordChange(pendingPasswordChange.user, pendingPasswordChange.password);
          }
        }}
        title={pendingPasswordChange ? "Защищенное действие: Смена пароля Superadmin" : "Защищенное действие: Редактирование Superadmin"}
      />

      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="delete-staff-modal">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
            
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{tt('Удалить сотрудника')}</h3>
              <p className="text-sm text-gray-600 mb-6">
                {tt('Вы уверены, что хотите удалить сотрудника')} <strong>{userToDelete.name}</strong>{tt('? Все клиенты, закрепленные за этим сотрудником, станут незакрепленными. Это действие нельзя отменить.')}
              </p>
            </div>

            {errors.general && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
                {errors.general}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setUserToDelete(null);
                  setErrors({});
                }}
                className="btn btn-secondary"
                data-testid="cancel-delete-staff"
              >
                {tt('Отмена')}
              </button>
              <button
                onClick={confirmDeleteUser}
                className="btn btn-danger"
                data-testid="confirm-delete-staff"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {tt('Удалить')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {permissionsUser && (
        <PermissionsModal
          user={permissionsUser}
          onClose={() => setPermissionsUser(null)}
          onSaved={() => { /* no reload needed, permissions are not shown in the list */ }}
        />
      )}
    </div>
  );
};

export default StaffPage;